/**
 * Centralized OCPP connection details shown to hosts when they need to point
 * their charger at the MeanderEV CSMS. Keeping this in one place means every
 * surface (charger card, registration screen, onboarding, activity log, etc.)
 * displays the exact same server URL / port / security settings.
 */

// The OCPP server URL is set explicitly per environment via
// REACT_NATIVE_OCPP_SERVER_URL (see .env.stage / .env.development /
// .env.base.production). Examples:
//   stage: wss://ocpp.stage.meanderev.co/ocpp
//   prod:  wss://ocpp.app.meanderev.co/ocpp
//
// If the env var is unset, fall back to deriving the host from the API server
// (prefix "ocpp." + append "/ocpp"), so the URL is still reasonable in local
// setups that don't define it.
const deriveFromApiServer = (): string => {
  const apiServer = process.env.REACT_NATIVE_API_SERVER || 'https://stage.meanderev.co';
  const apiHost = apiServer.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  return `wss://ocpp.${apiHost}/ocpp`;
};

/** Base MeanderEV OCPP server URL (no charger id). */
export const OCPP_SERVER_URL =
  process.env.REACT_NATIVE_OCPP_SERVER_URL || deriveFromApiServer();

/** TLS port used by the OCPP WebSocket endpoint. */
export const OCPP_SERVER_PORT = '443';

/** Security profile most chargers should select. */
export const OCPP_SECURITY_PROFILE = 'TLS with Basic Authentication';

/**
 * Page where hosts can download the MeanderEV server (CA) certificate for
 * chargers that require it to trust the OCPP TLS connection.
 */
export const OCPP_CA_CERTIFICATE_URL = 'https://meanderev.com/ocpp/';

/**
 * Some chargers require the charger id appended to the URL. When we know the
 * id (e.g. the serial the host registered with) we can show the explicit form.
 */
export const buildOcppUrlWithId = (chargerId?: string | null): string => {
  const id = (chargerId ?? '').trim();
  return id ? `${OCPP_SERVER_URL}/${id}` : `${OCPP_SERVER_URL}/<chargerID>`;
};
